// react
import React from 'react';

// tanstack
import {
  Link,
  useMatches,
  useMatchRoute,
  useParams,
} from '@tanstack/react-router';

// material ui icons
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';

// material ui
import {
  alpha,
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';

// relative
import type { NavItem as NavItemType } from './navigationTypes';

interface NavItemProps {
  item: NavItemType;
  depth?: number;
  drawerOpen?: boolean;
  isExpanded: (item: NavItemType) => boolean;
  onToggle: (itemId: string, currentExpandedState: boolean) => void;
  onPopperOpen: (
    event: React.MouseEvent<HTMLElement>,
    children: NavItemType[],
    itemId: string,
  ) => void;
  onPopperClose: (delay?: number) => void;
}

export const NavItem: React.FC<NavItemProps> = ({
  item,
  depth = 0,
  drawerOpen = true,
  isExpanded,
  onToggle,
  onPopperOpen,
  onPopperClose,
}) => {
  // This is here exclusively to make sure isactive is beeing recalculated when the route changes. Otherwise, the navitem will not update its active state when the route changes.
  const matches = useMatches();
  const matchRoute = useMatchRoute();
  const { accountId, energyGroupId } = useParams({ strict: false });
  const hasChildren = item.children && item.children.length > 0;
  const itemPaddingLeft = depth === 0 ? 2.5 : 2.5 + depth * 2;
  const selectedItemStyles = {
    '&.Mui-selected': {
      backgroundColor: (theme: Theme) =>
        alpha(theme.palette.primary.main, 0.12),
      '&:hover': {
        backgroundColor: (theme: Theme) =>
          alpha(theme.palette.primary.main, 0.18),
      },
    },
  };

  // or self
  const hasActiveDescendant = (navItem: NavItemType): boolean => {
    if (
      navItem.path &&
      matchRoute({
        to: navItem.path,
        params: { accountId },
        includeSearch: true,
        fuzzy: true,
      })
    )
      return true;
    if (!navItem.children?.length || !matches.length) return false;
    return navItem.children.some((child) => {
      if (
        child.path &&
        Boolean(
          matchRoute({
            to: child.path,
            params: { accountId, energyGroupId },
            includeSearch: true,
            fuzzy: true,
          }),
        )
      )
        return true;
      return hasActiveDescendant(child);
    });
  };
  const isActive = Boolean(
    (matches &&
      item.path &&
      matchRoute({
        to: item.path,
        params: { accountId, energyGroupId },
        fuzzy: true,
      })) ||
    (hasChildren && hasActiveDescendant(item)),
  );
  const hasActiveChildRoute = Boolean(hasChildren && hasActiveDescendant(item));
  const expanded = isExpanded(item);

  // Collapsed drawer with nested items - show popper on click
  if (!drawerOpen && hasChildren) {
    return (
      <Tooltip title={item.label} placement="right">
        <ListItemButton
          onClick={(e) =>
            !item.disabled && onPopperOpen(e, item.children!, item.id)
          }
          selected={isActive}
          disabled={item.disabled}
          sx={{
            minHeight: 48,
            justifyContent: 'center',
            borderRadius: 2,
            px: 2.5,
            '&.Mui-selected': {
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, 0.12),
              '&:hover': {
                backgroundColor: (theme) =>
                  alpha(theme.palette.primary.main, 0.18),
              },
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: 'auto',
              justifyContent: 'center',
            }}
          >
            {item.icon}
          </ListItemIcon>
        </ListItemButton>
      </Tooltip>
    );
  }

  // Collapsed drawer without nested items - simple navigation
  if (!drawerOpen && item.path) {
    const content = (
      <Tooltip title={item.label} placement="right">
        <ListItemButton
          selected={isActive}
          disabled={item.disabled}
          sx={{
            minHeight: 48,
            justifyContent: 'center',
            borderRadius: 2,
            px: 2.5,
            '&.Mui-selected': {
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, 0.12),
              '&:hover': {
                backgroundColor: (theme) =>
                  alpha(theme.palette.primary.main, 0.18),
              },
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: 'auto',
              justifyContent: 'center',
            }}
          >
            {item.icon}
          </ListItemIcon>
        </ListItemButton>
      </Tooltip>
    );

    if (item.disabled) {
      return content;
    }

    return (
      <Link
        to={item.path}
        activeProps={{
          style: { textDecoration: 'none', color: 'inherit' },
        }}
        inactiveProps={{
          style: { textDecoration: 'none', color: 'inherit' },
        }}
      >
        {({ isActive: linkIsActive }) => (
          <Tooltip title={item.label} placement="right">
            <ListItemButton
              selected={linkIsActive}
              sx={{
                minHeight: 48,
                justifyContent: 'center',
                borderRadius: 2,
                px: 2.5,
                '&.Mui-selected': {
                  backgroundColor: (theme) =>
                    alpha(theme.palette.primary.main, 0.12),
                  '&:hover': {
                    backgroundColor: (theme) =>
                      alpha(theme.palette.primary.main, 0.18),
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: 'auto',
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        )}
      </Link>
    );
  }

  // Expanded drawer - full display
  const listItemButton = (
    <ListItemButton
      onClick={() =>
        hasChildren && !item.disabled && onToggle(item.id, expanded)
      }
      selected={isActive}
      disabled={item.disabled}
      sx={{
        pl: itemPaddingLeft,
        borderRadius: 2,
        ...selectedItemStyles,
      }}
    >
      {item.icon && (
        <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
      )}
      <ListItemText
        primary={item.label}
        slotProps={{
          primary: {
            fontSize: depth > 0 ? '0.775rem' : '0.8375rem',
            fontWeight: depth > 0 ? 400 : 500,
          },
        }}
      />
    </ListItemButton>
  );

  const splitBranchItem =
    item.path && hasChildren ? (
      <Box
        onMouseEnter={(event) =>
          !item.disabled && onPopperOpen(event, item.children!, item.id)
        }
        onMouseLeave={() => onPopperClose(120)}
        sx={{ display: 'flex', alignItems: 'stretch', gap: 0.5 }}
      >
        {item.disabled ? (
          <ListItemButton
            selected={isActive}
            disabled
            sx={{
              flex: 1,
              minWidth: 0,
              pl: itemPaddingLeft,
              borderRadius: 2,
              ...selectedItemStyles,
            }}
          >
            {item.icon && (
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
            )}
            <ListItemText
              primary={item.label}
              slotProps={{
                primary: {
                  fontSize: depth > 0 ? '0.775rem' : '0.8375rem',
                  fontWeight: depth > 0 ? 400 : 500,
                },
              }}
            />
          </ListItemButton>
        ) : (
          <Link
            to={item.path}
            activeProps={{
              style: {
                textDecoration: 'none',
                color: 'inherit',
                flex: 1,
              },
            }}
            inactiveProps={{
              style: {
                textDecoration: 'none',
                color: 'inherit',
                flex: 1,
              },
            }}
          >
            <ListItemButton
              selected={isActive}
              sx={{
                width: '100%',
                minWidth: 0,
                pl: itemPaddingLeft,
                borderRadius: 2,
                ...selectedItemStyles,
              }}
            >
              {item.icon && (
                <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              )}
              <ListItemText
                primary={item.label}
                slotProps={{
                  primary: {
                    fontSize: depth > 0 ? '0.775rem' : '0.8375rem',
                    fontWeight: depth > 0 ? 400 : 500,
                  },
                }}
              />
              <MenuRoundedIcon fontSize="small" />
            </ListItemButton>
          </Link>
        )}
      </Box>
    ) : null;

  return (
    <React.Fragment key={item.id}>
      {splitBranchItem ??
        (item.path && !hasChildren ? (
          item.disabled ? (
            <ListItemButton
              selected={isActive}
              disabled
              sx={{
                pl: itemPaddingLeft,
                borderRadius: 2,
                ...selectedItemStyles,
              }}
            >
              {item.icon && (
                <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              )}
              <ListItemText
                primary={item.label}
                slotProps={{
                  primary: {
                    fontSize: depth > 0 ? '0.775rem' : '0.8375rem',
                    fontWeight: depth > 0 ? 400 : 500,
                  },
                }}
              />
            </ListItemButton>
          ) : (
            <Link
              to={item.path}
              activeProps={{
                style: { textDecoration: 'none', color: 'inherit' },
              }}
              inactiveProps={{
                style: { textDecoration: 'none', color: 'inherit' },
              }}
            >
              {({ isActive: linkIsActive }) => (
                <ListItemButton
                  selected={linkIsActive}
                  sx={{
                    pl: itemPaddingLeft,
                    borderRadius: 2,
                    ...selectedItemStyles,
                  }}
                >
                  {item.icon && (
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primary={item.label}
                    slotProps={{
                      primary: {
                        fontSize: depth > 0 ? '0.775rem' : '0.8375rem',
                        fontWeight: depth > 0 ? 400 : 500,
                      },
                    }}
                  />
                </ListItemButton>
              )}
            </Link>
          )
        ) : (
          listItemButton
        ))}

      {hasChildren && (
        <Collapse
          in={hasActiveChildRoute || expanded}
          timeout="auto"
          unmountOnExit
        >
          <List
            component="nav"
            sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
          >
            {item.children!.map((child) => (
              <NavItem
                key={child.id}
                item={child}
                depth={depth + 1}
                drawerOpen={drawerOpen}
                isExpanded={isExpanded}
                onToggle={onToggle}
                onPopperOpen={onPopperOpen}
                onPopperClose={onPopperClose}
              />
            ))}
          </List>
        </Collapse>
      )}
    </React.Fragment>
  );
};
